import {
  AdmissionAggregateRow,
  AdmissionArrangeBy,
  AdmissionMetric,
  DATE_FORMAT_MONTH_FNS,
  DATE_FORMAT_MONTH_SHORT_FNS,
} from '@schooly/api';
import { theme } from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format, setMonth } from 'date-fns';

import { ChartSeries, createSpacerSeries } from '../utils';

export type AdmissionDashboardChartSeriesData = {
  name: string;
  value: number | null;
  tooltipData: Array<{
    label: string;
    value?: number | null;
    color: string;
  }>;
};

type AdmissionDashboardChartSeries = ChartSeries<AdmissionDashboardChartSeriesData>;

export const dashboardChartConfig = {
  barWidth: 42,
  spacerWidth: 16,
  barCategoryGap: '30%',
  barGap: '20%',
  grid: {
    top: 20,
    left: 30,
    right: 0,
    bottom: 40,
  },
} as const;

type CompareSeriesData = {
  current: AdmissionDashboardChartSeriesData;
  compare: AdmissionDashboardChartSeriesData;
};

export const dashboardChartBorderRadius: [number, number, number, number] = [4, 4, 4, 4];

export const getDashboardMetricsColor = (): Record<AdmissionMetric, string> => ({
  applied: theme.palette.primary.main,
  offered: theme.palette.warning.main,
  accepted: theme.palette.success.main,
});

export const getDashboardBarColor = (): Record<
  AdmissionMetric,
  { current: string; compared: string }
> => ({
  applied: {
    current: theme.palette.common.main2,
    compared: theme.palette.common.main4,
  },
  offered: {
    current: theme.palette.common.orange2,
    compared: theme.palette.common.orange4,
  },
  accepted: {
    current: theme.palette.common.green7,
    compared: theme.palette.common.green6,
  },
});

export function formatQuarterLabel(str: string): string {
  return `Q${str}`;
}

export function getAdmissionDashboardMonthName(monthNumber: number): string {
  const baseDate = newDateTimezoneOffset();
  const date = setMonth(baseDate, monthNumber - 1);
  return format(date, DATE_FORMAT_MONTH_FNS);
}

export function buildDashboardTooltipData(
  metric: AdmissionMetric,
  formatMessage: (id: string) => string,
  currentRow: AdmissionAggregateRow,
  comparedRow: AdmissionAggregateRow,
  arrangeBy: AdmissionArrangeBy,
): AdmissionDashboardChartSeriesData['tooltipData'] {
  const getTooltipLabel = (row: AdmissionAggregateRow) => {
    return arrangeBy === 'month'
      ? formatMonthLabel(row.meta.year, row.meta.period)
      : formatQuarterLabel(`${row.meta.period} ${row.meta.year}`);
  };
  return [
    {
      label: formatMessage(metric),
      color: getDashboardMetricsColor()[metric],
    },
    {
      label: getTooltipLabel(currentRow),
      value: currentRow.value ?? null,
      color: theme.palette.text.primary,
    },
    {
      label: getTooltipLabel(comparedRow),
      value: comparedRow.value ?? null,
      color: theme.palette.common.grey3,
    },
  ];
}

export function createDashboardChartBarSeries(
  name: string,
  data: AdmissionDashboardChartSeriesData[],
  color: string | ((params: { dataIndex: number }) => string),
  silent = false,
): AdmissionDashboardChartSeries {
  const { barWidth } = dashboardChartConfig;
  return {
    name,
    type: 'bar',
    barWidth,
    itemStyle: {
      borderRadius: dashboardChartBorderRadius,
      color,
    },
    ...(silent && { silent: true }),
    data,
  };
}

function getUniqueAndSorted(values: number[]): number[] {
  return Array.from(new Set(values)).sort((a, b) => a - b);
}

type FindPeriodRowParams = {
  rows: AdmissionAggregateRow[];
  metric: AdmissionMetric;
  year: number;
  period?: number;
};

function findPeriodRow({
  rows,
  metric,
  year,
  period,
}: FindPeriodRowParams): AdmissionAggregateRow | undefined {
  return rows.find(
    (r) => r.name === metric && r.meta.year === year && (period ? r.meta.period === period : true),
  );
}

function formatMonthLabel(year: number, period: number): string {
  return format(setMonth(new Date(year, 0), period - 1), DATE_FORMAT_MONTH_SHORT_FNS);
}

export function transformDashboardRowsToChartSeries(
  rows: AdmissionAggregateRow[],
  arrangeBy: AdmissionArrangeBy,
  formatMessage: (id: string) => string,
  metrics: AdmissionMetric[],
): { series: AdmissionDashboardChartSeries[]; categories: string[] } {
  const years = getUniqueAndSorted(rows.map((r) => r.meta.year));
  const [compareYear, currentYear] = years;

  if (!compareYear || !currentYear) {
    return { series: [], categories: [] };
  }

  if (arrangeBy === 'month') {
    const periods = getUniqueAndSorted(rows.map((r) => r.meta.period));
    const categories = periods.map(getAdmissionDashboardMonthName);
    const series: AdmissionDashboardChartSeries[] = [];

    for (const [index, metric] of metrics.entries()) {
      const data: CompareSeriesData[] = [];

      for (const period of periods) {
        const current = findPeriodRow({ rows, metric, year: currentYear, period });
        const compare = findPeriodRow({ rows, metric, year: compareYear, period });

        if (!current || !compare) continue;

        const tooltipData = buildDashboardTooltipData(
          metric,
          formatMessage,
          current,
          compare,
          arrangeBy,
        );

        data.push({
          current: { name: formatMessage(metric), value: current.value, tooltipData },
          compare: { name: formatMessage(metric), value: compare.value, tooltipData },
        });
      }

      series.push(
        createDashboardChartBarSeries(
          formatMessage(metric),
          data.map((d) => d.current),
          getDashboardBarColor()[metric].current,
        ),
        createDashboardChartBarSeries(
          formatMessage(metric),
          data.map((d) => d.compare),
          getDashboardBarColor()[metric].compared,
        ),
      );

      if (index < metrics.length - 1) {
        series.push(createSpacerSeries(metric, dashboardChartConfig.spacerWidth, periods.length));
      }
    }

    return { series, categories };
  }

  const categories = metrics.map(formatMessage);
  const data: CompareSeriesData[] = [];
  for (const metric of metrics) {
    const current = findPeriodRow({ rows, metric, year: currentYear });
    const compare = findPeriodRow({ rows, metric, year: compareYear });

    if (!current || !compare) continue;

    const tooltipData = buildDashboardTooltipData(
      metric,
      formatMessage,
      current,
      compare,
      arrangeBy,
    );

    data.push({
      current: { name: formatMessage(metric), value: current.value, tooltipData },
      compare: { name: formatMessage(metric), value: compare.value, tooltipData },
    });
  }

  const series = [
    createDashboardChartBarSeries(
      'Current',
      data.map((d) => d.current),
      (params) => {
        const metric = metrics[params.dataIndex];
        return metric ? getDashboardBarColor()[metric].current : theme.palette.primary.main;
      },
    ),
    createDashboardChartBarSeries(
      'Compared',
      data.map((d) => d.compare),
      (params) => {
        const metric = metrics[params.dataIndex];
        return metric ? getDashboardBarColor()[metric].compared : theme.palette.common.grey3;
      },
    ),
  ];

  return { series, categories };
}
